import { Spin } from 'antd';
import { DateTime } from 'luxon';
import murmurhash from 'murmurhash';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Environment } from 'relay-runtime';

import { processCompletionPercentage, processOrderingItemsAndSetNumbersWBS } from '../gantChangeData';
import { ComponentsDispatch, RootState } from '../store';
import { setterGantItems } from '../store/slices/gantItemsSlice';
import { setMappingItemIdIndex, setterGant } from '../store/slices/gantSlice';
import { getExpandedItems, isItemExpanded } from '../table/functions';
import {
    ItemType,
    MappingItemIdHideCollapse,
    OnScheduleItemsImport,
    ScheduleItemType,
    SettingGantType,
} from '../types';
import GantChart from './gantChart';
import GantTable from './gantTable';
import GantTableContainer from './gantTableContainer';

export interface IGant {
    inputItems?: ItemType[];
    paddingBottom?: number;
    environment?: Environment;
    newItem?: ScheduleItemType;
    isEditable?: boolean;
    authorId: string;
    mode: 'chart' | 'table';
    onFinishLoadData?: () => void;
    onScheduleItemsImport?: OnScheduleItemsImport;
}

let hashItemsList: number;
let hashHiddenList: number;

const Gant: React.FC<IGant> = ({
    paddingBottom = 0,
    newItem,
    inputItems = [],
    isEditable,
    authorId,
    mode = 'chart',
    onScheduleItemsImport,
}) => {
    const dispatch = useDispatch<ComponentsDispatch>();

    const gantEditScheduleItem = useSelector<
        RootState,
        | {
              userId: string;
              projectId: string;
          }[]
        | undefined
    >((state) => state.gant.settingGant?.data.gantEditScheduleItem);

    const startDateExtend = useSelector<RootState, string>((state) => state.gant.startDateExtend);
    const endDateExtend = useSelector<RootState, string>((state) => state.gant.endDateExtend);
    const settingGant = useSelector<RootState, SettingGantType | null>((state) => state.gant.settingGant);
    const spinner = useSelector<RootState, boolean>((state) => state.spinner.spinner);
    const wasChanged = useSelector<RootState, boolean>((state) => state.gantItems.wasChanged);
    const mappingItemIdHideCollapse = useSelector<RootState, MappingItemIdHideCollapse>(
        (state) => state.gant.mappingItemIdHideCollapse,
    );

    const wasChangedRef = useRef<boolean>(false);

    wasChangedRef.current = wasChanged;

    const [dates, setDates] = useState<DateTime[]>([]);

    const start = DateTime.fromISO(startDateExtend);
    const end = DateTime.fromISO(endDateExtend);

    const fullLengthGantInDays = Math.ceil(end.diff(start, 'days').days) + 1;

    useEffect(() => {
        dispatch(setterGant({ paddingBottom }));
    }, [paddingBottom]);

    useEffect(() => {
        dispatch(
            setterGant({
                mappingItemIdHideCollapse: settingGant?.data.gantVisibleItems?.[authorId] || {},
            }),
        );
    }, [settingGant]);

    useEffect(() => {}, [isEditable]);

    useEffect(() => {
        dispatch(setterGant({ authorId }));
    }, [authorId]);

    useEffect(() => {
        dispatch(setterGant({ newExternalItem: newItem || null }));
    }, [newItem]);

    useEffect(() => {
        const actualMappingItemIdHideCollapse =
            settingGant?.data.gantVisibleItems?.[authorId] ?? mappingItemIdHideCollapse;

        if (!inputItems.length || wasChangedRef.current) {
            return;
        }

        const newHashItemsList = murmurhash.v3(JSON.stringify(inputItems));
        const newHashHiddenList = murmurhash.v3(JSON.stringify(actualMappingItemIdHideCollapse));

        if (hashItemsList !== newHashItemsList || hashHiddenList !== newHashHiddenList) {
            hashItemsList = newHashItemsList;
            hashHiddenList = newHashHiddenList;

            if (!hashItemsList) {
                return;
            }

            let index = 0;
            const { mappingScheduleItemIdIndex, newItems, newInputItems } = processOrderingItemsAndSetNumbersWBS(
                inputItems.map((item) => ({
                    ...item,
                    hideForCollapse: actualMappingItemIdHideCollapse[item.id],
                    idIndex: !item.hideForCollapse ? index++ : -1,
                })),
            );

            index = 0;
            const correctlyExpandedItems = newItems.map((item) => {
                const isExpanded = isItemExpanded(item, newItems, mappingScheduleItemIdIndex);
                return {
                    ...item,
                    hideForCollapse: !isExpanded,
                    idIndex: isExpanded ? index++ : -1,
                };
            });

            const newItemsWithPercentage = settingGant
                ? processCompletionPercentage(
                      correctlyExpandedItems,
                      settingGant.data.statusTaskDone.toString(),
                      mappingScheduleItemIdIndex,
                  )
                : correctlyExpandedItems;
            dispatch(
                setterGantItems({
                    inputItems: newInputItems,
                    itemsList: newItemsWithPercentage,
                }),
            );

            dispatch(
                setterGant({
                    countVisibleRow: getExpandedItems(newItemsWithPercentage, mappingScheduleItemIdIndex).length,
                    mappingInputItemIdIndex: mappingScheduleItemIdIndex,
                }),
            );

            dispatch(setMappingItemIdIndex(mappingScheduleItemIdIndex));
        }
    }, [inputItems, mappingItemIdHideCollapse]); // new version and new approach

    useEffect(() => {
        if (startDateExtend) {
            const newDates = [];
            for (let i = 0; i <= fullLengthGantInDays; i++) {
                newDates.push(start.plus({ days: i }));
            }
            setDates(newDates);
        }
    }, [startDateExtend, endDateExtend]);

    const spinnerTip = !!inputItems.length ? 'Saving...' : 'Loading...';

    return (
        <Spin
            tip={spinnerTip}
            spinning={spinner}
            style={{
                width: '100%',
            }}
        >
            {mode === 'chart' ? (
                <GantChart dates={dates} isInputItemsExists={!!inputItems.length} paddingBottom={paddingBottom} />
            ) : (
                <GantTableContainer>
                    <GantTable
                        onScheduleItemsImport={onScheduleItemsImport}
                        gantEditScheduleItem={gantEditScheduleItem}
                        authorId={authorId}
                    />
                </GantTableContainer>
            )}
        </Spin>
    );
};

export default Gant;
